import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import dayjs from "dayjs";
import {
  BookingsDialog,
} from "src/components/Dialogs/BookingsDialog/BookingsDialog";
import { RightBookingsDrawer } from "src/components/RightDrawer/RightBookingsDrawer";
import { DefaultSingleSelectorWithoutBody } from "src/components/Selector/DefaultSingleSelectorWithoutBody";
import { BookingsSearchTypes } from "src/components/ToggleBox/ToggleBox.types";
import { ToggleViewType } from "src/constants/services";
import { UserRoles } from "src/constants/user";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import {
  ChevronDown,
  ChevronUp,
} from "src/Icons";
import {
  getBookings,
  getFilteredBookings,
  updateSearchParams,
} from "src/store/reducers/bookingsSlice";
import {
  showBookingsDialog,
} from "src/store/reducers/dialogSlice";
import { openDrawer } from "src/store/reducers/drawerSlice";
import { getEmployees } from "src/store/reducers/employeesSlice";
import { getServices } from "src/store/reducers/servicesSlice";
import { getStudios } from "src/store/reducers/studiosSlice";
import { setSelectedRow } from "src/store/reducers/tableSlice";
import { ICommonServiceType } from "src/store/types/commonTypes";
import { StudioType } from "src/store/types/studiosTypes";
import { elementStyle, getArrayForBookingSingleSelector } from "src/utils/helpers";
import { getValidMultiSearchParams } from "src/utils/search";

import {
  EmptyPageState,
  MainPage,
  Table,
  Timesheet,
  TimeSheetHeader,
  ViewChangePicker,
} from "../../components";
import { SortByMenuStyleProps } from "../CommonComponentsStyles";

import { arrayStatus, columns } from "./BookingsScreen.data";
import { SortTypes } from "./BookingsScreen.types";

export const BookingsScreen = () => {
  const dispatch = useAppDispatch();

  const {
    bookings,
    filteredBookings,
    searchParams: isSearching,
  } = useAppSelector((state) => state.bookings);
  const { user } = useSelector((state: any) => state.auth);
  // @ts-ignore
  const role = UserRoles[user?.role];

  const { employees } = useAppSelector((state) => state.employees);
  const { services } = useAppSelector((state) => state.services);
  const { studios } = useAppSelector((state) => state.studios);
  const {
    isBookingsLoading,
    isFilteredBookingsLoading,
    selectedStudioBoxValue: toogleStudioValue,
    selectedServiceBoxValue: toogleServiceValue,
    selectedEmployeeBoxValue: toogleEmployeeValue,
    selectedStatusBoxValue: toogleStatusValue,
    sortByValue: sortValue,
  } = useAppSelector((state) => state.bookings);

  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [sortBy, setSortBy] = useState<string>(sortValue);
  const [studio, setStudio] = useState(toogleStudioValue);
  const [service, setService] = useState(toogleServiceValue);
  const [employee, setEmployee] = useState(toogleEmployeeValue);
  const [status, setStatus] = useState(toogleStatusValue);
  const [isSortSelectOpen, setIsSortSelectOpen] = useState<boolean>(false);
  const [toggleView, setToggleView] = useState<ToggleViewType>(
    ToggleViewType.row
  );

  const notSelectedVariant = (type: number, array: StudioType[] | ICommonServiceType[] | {id: string, title: string}[]) => {
    if (type === 0) {
      return getArrayForBookingSingleSelector(array);
    } else {
      return getArrayForBookingSingleSelector([{ id: 0, title: "Not selected" }, ...array]);
    }}

  const paramsCatalogue = {
    sortParams: {
      [SortTypes.ID]: "id",
      [SortTypes.FROM]: "from",
    },
    searchParams: {
      [BookingsSearchTypes.MASTER_ID]: "masterId",
      [BookingsSearchTypes.SERVICE_ID]: "serviceId",
      [BookingsSearchTypes.STUDIO_ID]: "studioId",
      [BookingsSearchTypes.STATUS]: "status",
    },
  };

  const multiSearchParams =
      {
        [BookingsSearchTypes.MASTER_ID]: employee?.toString(),
        [BookingsSearchTypes.SERVICE_ID]: service?.toString(),
        [BookingsSearchTypes.STUDIO_ID]: studio?.toString(),
        [BookingsSearchTypes.STATUS]: arrayStatus[+status - 1]?.status,
      }

  const { isBookingsDialogOpen } = useSelector((state: any) => state.dialog);

  const handleOpenBookingsDialog = () => {
    dispatch(showBookingsDialog());
  };

  const handleUpdateSearchParams = () => {
    return updateSearchParams({
      toogleStudioValue,
      toogleServiceValue,
      toogleEmployeeValue,
      toogleStatusValue,
      sortBy,
    })
  }

  const handleChangeStatus = (newValue: number) => {
    setStatus(newValue);
    const status = arrayStatus[+newValue - 1]?.status;
    dispatch(handleUpdateSearchParams());
    dispatch(
      getFilteredBookings(
        getValidMultiSearchParams (
          paramsCatalogue,
          {...multiSearchParams, [BookingsSearchTypes.STATUS]: status},
          sortBy
        )
      )
    );
  };

  const handleChangeEmployee = (newValue: number) => {
    setEmployee(newValue);
    dispatch(handleUpdateSearchParams());
    dispatch(
      getFilteredBookings(
        getValidMultiSearchParams (
          paramsCatalogue,
          {...multiSearchParams, [BookingsSearchTypes.MASTER_ID]: newValue?.toString()},
          sortBy
        )
      )
    );
  };

  const handleChangeService = (newValue: number) => {
    setService(newValue);
    dispatch(handleUpdateSearchParams());
    dispatch(
      getFilteredBookings(
        getValidMultiSearchParams (
          paramsCatalogue,
          {...multiSearchParams, [BookingsSearchTypes.SERVICE_ID]: newValue?.toString()},
          sortBy
        )
      )
    );
  };

  const handleChangeStudio = (newValue: number) => {
    setStudio(newValue);
    dispatch(handleUpdateSearchParams());
    dispatch(
      getFilteredBookings(
        getValidMultiSearchParams (
          paramsCatalogue,
          {...multiSearchParams, [BookingsSearchTypes.STUDIO_ID]: newValue?.toString()},
          sortBy
        )
      )
    );
  };

  const handleChangeToggleView = (newValue: ToggleViewType) => {
    setToggleView(newValue);
  };

  const handleChangeSortBy = (event: SelectChangeEvent) => {
    setSortBy(event.target.value as SortTypes);
    dispatch(handleUpdateSearchParams());
    dispatch(
      getFilteredBookings(
        getValidMultiSearchParams (
          paramsCatalogue,
          multiSearchParams,
          event.target.value
        )
      )
    );
  };

  const onRowClick = (params: any) => {
    dispatch(setSelectedRow(params.row));
    dispatch(openDrawer());
  };

  useEffect(() => {
    dispatch(getBookings());
    dispatch(getStudios());
    dispatch(getEmployees());
    dispatch(getServices());
  }, []);

  const addButtonText = "Add booking";

  return (
    <MainPage
      title="Bookings"
      addButtonText={addButtonText}
      isDialogOpen={isBookingsDialogOpen}
      isShouldShowEmptyState={!bookings.length && !isBookingsLoading}
      onAddButtonClick={handleOpenBookingsDialog}
      hiddenElement={() =>
      {
        return elementStyle({
          role,
          roleType: [ "SUPER_ADMIN", "MASTER" ],
          firstOption: "none",
          lastOption: "inline-flex",
        });
      }
      }
      DialogComp={<BookingsDialog isOpen={isBookingsDialogOpen} />}
      DrawerComp={<RightBookingsDrawer />}
      EmptyStateComp={
        <EmptyPageState
          image="/images/empty-page-states/calendar.png"
          title="There are no scheduled appointments  currently in the calendar"
          subTitle={
            "To begin managing your salon's schedule, you can add new appointments, set working hours for your studios, assign staff members to specific time slots, and keep track of client bookings."
          }
          hiddenElement={() =>
            elementStyle({
              role,
              roleType: ["SUPER_ADMIN", "MASTER"],
              firstOption: "none",
              lastOption: "inline-flex",
            })
          }
          buttonText={addButtonText}
          onButtonClick={handleOpenBookingsDialog}
        />
      }
    >
      <main className="main-page">
        {toggleView === ToggleViewType.colum ? (
          <TimeSheetHeader
            date={selectedDate}
            onDateChanged={(newValue: dayjs.Dayjs) => {
              setSelectedDate(newValue);
            }}
            onClickBackward={() => {
              setSelectedDate((date) => date.subtract(1, "day"));
            }}
            onClickForward={() => {
              setSelectedDate((date) => date.add(1, "day"));
            }}
          />
        ) : null}
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: 1,
              flexWrap: "wrap",
              minWidth: "405px"
            }}
          >
            <DefaultSingleSelectorWithoutBody
              onChange={handleChangeStudio}
              label="Studio"
              value={studio}
              valueToSelect={notSelectedVariant(studio, studios)}
            />

            <DefaultSingleSelectorWithoutBody
              onChange={handleChangeService}
              label="Service"
              value={service}
              valueToSelect={notSelectedVariant(service, services)}
            />

            <DefaultSingleSelectorWithoutBody
              onChange={handleChangeEmployee}
              label="Employee"
              value={employee}
              valueToSelect={
                notSelectedVariant(
                  employee,
                  employees.map(({ id, firstName }) => ({
                    id,
                    title: firstName,
                  })))
              }
            />

            <DefaultSingleSelectorWithoutBody
              onChange={handleChangeStatus}
              label="Status"
              value={status}
              valueToSelect={
                notSelectedVariant(
                  status,
                  arrayStatus.map(({ id, statusValue }: any) => ({
                    id,
                    title: statusValue,
                  })))
              }
            />
          </Box>

          <Box sx = {{ display: "flex", }}>
            <Box
              sx={{
                display: "flex",
                padding: "24px 16px 0 16px",
              }}
            >
              <ViewChangePicker
                toggleView={toggleView}
                onChange={handleChangeToggleView}
              />
            </Box>

            <Box
              sx={{
                minWidth: 120,
                margin: "24px 16px 0 0",
              }}
            >
              <FormControl fullWidth>
                <InputLabel id="select-sort-by-label">Sort by</InputLabel>
                <Select
                  labelId="select-sort-by-label"
                  id="select-sort-by"
                  value={sortBy}
                  label="Sort by"
                  onOpen={() => setIsSortSelectOpen(true)}
                  onClose={() => setIsSortSelectOpen(false)}
                  onChange={handleChangeSortBy}
                  IconComponent={() => (
                    <>
                      {isSortSelectOpen ? (
                        <ChevronUp fontSize="inherit" />
                      ) : (
                        <ChevronDown fontSize="inherit" />
                      )}
                    </>
                  )}
                  sx={{
                    borderRadius: "8px",
                    color: "#1F1F1F",
                    fontWeight: "500",
                    height: "48px",

                    "& .MuiSvgIcon-root": {
                      marginRight: "14px",
                      color: isSortSelectOpen ? "#BF295C" : "#676767",
                    },
                  }}
                  MenuProps={SortByMenuStyleProps}
                >
                  <MenuItem disableRipple value={SortTypes.ID}>
                    Id
                  </MenuItem>
                  <MenuItem disableRipple value={SortTypes.FROM}>
                    From
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Box>

        <Box
          style={{
            padding: "32px 16px",
            paddingBottom: bookings.length === 0 ? "72px" : undefined,
          }}
        >
          {toggleView === ToggleViewType.colum ? (
            <Timesheet date={selectedDate} />
          ) : (
            <Table
              rows={isSearching ? filteredBookings : bookings}
              loading={isBookingsLoading || isFilteredBookingsLoading}
              columns={columns}
              onRowClick={onRowClick}
            />
          )}
        </Box>
      </main>
    </MainPage>
  );
};
